import { useState, useEffect, useCallback } from 'react';
import { useApi } from '@/hooks/useApi';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import { Building2, Plus, X, Loader2 } from 'lucide-react';
import { NodeEntity } from '@/types/node.types';

interface Assignment {
  id: string;
  tenant_id: string;
  node_id: string;
  is_active: boolean;
  node?: NodeEntity;
}

interface TenantFCAssignmentProps {
  tenantId: string;
}

export function TenantFCAssignment({ tenantId }: TenantFCAssignmentProps) {
  const api = useApi();
  const [assignments, setAssignments] = useState<Assignment[]>([]);
  const [hasFetched, setHasFetched] = useState(false);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [allFCs, setAllFCs] = useState<NodeEntity[]>([]);
  const [selectedFCId, setSelectedFCId] = useState<string>('');
  const [loadingFCs, setLoadingFCs] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [deactivating, setDeactivating] = useState<string | null>(null);

  const fetchAssignments = useCallback(async () => {
    try {
      const result: any = await api.get('/api/v1/dashboard/tenant-fulfillment-assignments', {
        tenant_id: tenantId,
      });
      const list = result?.data ?? result ?? [];
      setAssignments(Array.isArray(list) ? list : []);
    } catch (error) {
      console.error('Failed to fetch FC assignments:', error);
    } finally {
      setHasFetched(true);
    }
  }, [tenantId]);

  useEffect(() => {
    fetchAssignments();
  }, [fetchAssignments]);

  const fetchAllFCs = async () => {
    setLoadingFCs(true);
    try {
      const result: any = await api.get('/api/v1/dashboard/nodes', {
        type: 'fulfillment_center',
      });
      const list = result?.data ?? result ?? [];
      setAllFCs(Array.isArray(list) ? list : []);
    } catch (error) {
      toast.error('فشل في جلب مراكز الفرز');
    } finally {
      setLoadingFCs(false);
    }
  };

  const handleOpenAssignModal = () => {
    setSelectedFCId('');
    setShowAssignModal(true);
    fetchAllFCs();
  };

  const handleAssign = async () => {
    if (!selectedFCId) {
      toast.error('يرجى اختيار مركز فرز');
      return;
    }
    setSubmitting(true);
    try {
      await api.post('/api/v1/dashboard/tenant-fulfillment-assignments', {
        tenant_id: tenantId,
        node_id: selectedFCId,
      });
      toast.success('تم تعيين مركز الفرز بنجاح');
      setShowAssignModal(false);
      fetchAssignments();
    } catch (error: any) {
      const message = error?.response?.data?.message || 'فشل في تعيين مركز الفرز';
      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeactivate = async (assignmentId: string) => {
    setDeactivating(assignmentId);
    try {
      await api.patch(`/api/v1/dashboard/tenant-fulfillment-assignments/${assignmentId}`, {
        is_active: false,
      });
      toast.success('تم إلغاء تعيين مركز الفرز');
      fetchAssignments();
    } catch (error: any) {
      const message = error?.response?.data?.message || 'فشل في إلغاء التعيين';
      toast.error(message);
    } finally {
      setDeactivating(null);
    }
  };

  const activeAssignments = assignments.filter((a) => a.is_active);

  return (
    <>
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0">
          <CardTitle className="flex items-center gap-2 text-base font-semibold">
            <Building2 className="h-4 w-4" />
            مراكز الفرز المعينة
          </CardTitle>
          <Button size="sm" onClick={handleOpenAssignModal} className="gap-1">
            <Plus className="h-4 w-4" />
            تعيين مركز فرز
          </Button>
        </CardHeader>
        <CardContent>
          {!hasFetched ? (
            <p className="text-sm text-muted-foreground text-center py-4">جاري التحميل...</p>
          ) : activeAssignments.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-8 text-center">
              <Building2 className="h-12 w-12 text-muted-foreground/40 mb-3" />
              <p className="text-sm text-muted-foreground">لا يوجد مركز فرز معين لهذا العميل</p>
              <Button
                size="sm"
                variant="outline"
                onClick={handleOpenAssignModal}
                className="mt-4 gap-1"
              >
                <Plus className="h-4 w-4" />
                تعيين مركز فرز
              </Button>
            </div>
          ) : (
            <div className="space-y-3">
              {activeAssignments.map((assignment) => (
                <div
                  key={assignment.id}
                  className="flex items-center justify-between rounded-lg border p-3"
                >
                  <div className="flex items-center gap-3">
                    <Building2 className="h-5 w-5 text-primary" />
                    <div>
                      <p className="font-medium text-sm">
                        {assignment.node?.name || assignment.node_id}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {assignment.node?.code || '-'} · {assignment.node?.city_name?.ar || '-'}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge variant="default" className="text-xs">
                      نشط
                    </Badge>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleDeactivate(assignment.id)}
                      disabled={deactivating === assignment.id}
                      title="إلغاء التعيين"
                      className="h-8 w-8 text-destructive hover:text-destructive"
                    >
                      {deactivating === assignment.id ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <X className="h-4 w-4" />
                      )}
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Assign Modal */}
      <Dialog open={showAssignModal} onOpenChange={setShowAssignModal}>
        <DialogContent className="sm:max-w-[450px]" dir="rtl">
          <DialogHeader>
            <DialogTitle>تعيين مركز فرز</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <div className="space-y-2">
              <label className="text-sm font-medium">مركز الفرز</label>
              {loadingFCs ? (
                <p className="text-sm text-muted-foreground">جاري تحميل مراكز الفرز...</p>
              ) : (
                <Select value={selectedFCId} onValueChange={setSelectedFCId}>
                  <SelectTrigger>
                    <SelectValue placeholder="اختر مركز الفرز" />
                  </SelectTrigger>
                  <SelectContent>
                    {allFCs.map((fc) => (
                      <SelectItem key={fc.id} value={fc.id}>
                        {fc.name} ({fc.code})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>
            <div className="flex gap-3 pt-2">
              <Button
                onClick={handleAssign}
                disabled={submitting || !selectedFCId}
                className="flex-1"
              >
                {submitting && <Loader2 className="ms-2 h-4 w-4 animate-spin" />}
                تعيين
              </Button>
              <Button variant="outline" onClick={() => setShowAssignModal(false)}>
                إلغاء
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
